import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { apiList } from "../../helpers/apiList";
import { axiosPostApi } from "../../helpers/interceptorServices";
import CustomAlert from "../../helpers/CustomAlert";
import { CircularProgress } from "@mui/material";
import AddUserIcon from "../../assets/Icons/add-user-img 1.svg";
import LeftCircle from "../../assets/Icons/Left Circle.svg";
import RightCircle from "../../assets/Icons/Right Circle.svg";

const TemplateModal = ({
  show,
  handleClose,
  activeTab,
  customerCode,
  name,
  TempDetails,
  modalType,
  handleShowTable,
}) => {
  const loggedInUserName = sessionStorage.getItem("username");
  const [entityIds, setEntityIds] = useState([]);
  const [senderIds, setSenderIds] = useState([]); // Store all sender IDs
  const [selectedSenderId, setSelectedSenderId] = useState(""); // Track selected sender ID
  const [senderIdError, setSenderIdError] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [templateName, setTemplateName] = useState("");
  const [templateNameError, setTemplateNameError] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [templateIdError, setTemplateIdError] = useState("");
  const [templateType, setTemplateType] = useState("");
  const [templateTypeError, setTemplateTypeError] = useState("");
  const [smsContent, setSmsContent] = useState("");
  const [smsDesc, setSmsDesc] = useState("");
  const [smsContentError, setSmsContentError] = useState("");
  const [loading, setLoading] = useState(false);
  const [dropDownOpen, setDropDownOpen] = useState(false);

  const isReadOnly = !!TempDetails;

  useEffect(() => {
    if (activeTab === "template") {
      fetchTemplateData();
    }
  }, [activeTab]); // The effect will re-run only if activeTab changes

  const fetchTemplateData = async () => {
    const data = {
      username: loggedInUserName,
      customerCode: customerCode,
    };

    try {
      const response = await axiosPostApi(apiList.DLT_FETCH_SENDER, data);
      const fetchedData = response?.data?.data;

      if (!fetchedData || fetchedData.length === 0) {
        setErrorMessage("Please, First add an Entity ID or Sender ID.");
      } else {
        const entityIdString = fetchedData[0].entityId || "";
        setEntityIds(entityIdString);
        const senderIdsArray = fetchedData?.map((item) => item.sendorId);
        setSenderIds(senderIdsArray); // Store array of all sender IDs
        setErrorMessage("");
      }
    } catch (error) {
      console.error("Failed to fetch data", error);
      setErrorMessage("Error fetching data. Please try again.");
    }
  };

  const handleSenderIdChange = (e) => {
    setSelectedSenderId(e.target.value);
  };

  const validateTemplateName = () => {
    if (!templateName) {
      setTemplateNameError("Template Name is required");
      return false;
    } else if (templateName.length < 2 || templateName.length > 50) {
      setTemplateNameError("Template Name must be 2 to 50 characters long");
      return false;
    } else if (!/^[A-Za-z0-9 ]+$/.test(templateName)) {
      setTemplateNameError(
        "Template Name can only contain letters, numbers, and spaces"
      );
      return false;
    } else if (/^\s|\s$/.test(templateName)) {
      setTemplateNameError("Template Name cannot start or end with spaces");
      return false;
    } else if (/\s{2,}/.test(templateName)) {
      setTemplateNameError(
        "Template Name cannot have multiple consecutive spaces"
      );
      return false;
    } else if (/^(\d)\1+$/.test(templateName)) {
      setTemplateNameError("Template Name cannot be the same digit repeated");
      return false;
    } else {
      setTemplateNameError("");
    }
  };

  const validateTemplateId = () => {
    if (!templateId) {
      setTemplateIdError("Template ID is required");
      return false;
    } else if (!/^[0-9]+$/.test(templateId)) {
      setTemplateIdError("Template ID must contain digits only (0-9)");
      return false;
    } else if (templateId.length < 6 || templateId.length > 20) {
      setTemplateIdError("Template ID must be between 6 and 20 digits long");
      return false;
    } else if (
      /^(?:0123456789|1234567890|9876543210|0987654321|\d)\d*$/.test(templateId)
    ) {
      setTemplateIdError(
        "Template ID cannot be a continuous ascending or descending sequence"
      );
      return false;
    } else if (/^(\d)\1+$/.test(templateId)) {
      setTemplateIdError("Template ID cannot be the same digit repeated");
      return false;
    } else {
      setTemplateIdError("");
      return true;
    }
  };

  const validateTemplateType = () => {
    if (!templateType) {
      setTemplateTypeError("Select Template type...");
      return false;
    } else {
      setTemplateTypeError("");
      return true;
    }
  };

  const validateSenderId = () => {
    if (!selectedSenderId) {
      setSenderIdError("Select Sender ID...");
      return false;
    } else {
      setSenderIdError("");
      return true;
    }
  };

  const validateSmsContent = () => {
    if (!smsContent) {
      setSmsContentError("Template Content is required");
      return false;
    } else if (smsContent.length < 3 || smsContent.length > 300) {
      setSmsContentError("Template Content must be 3 to 300 characters long");
      return false;
    } else {
      setSmsContentError("");
      return true;
    }
  };

  const handleAddTemplate = async (e) => {
    e.preventDefault();

    // Perform all validations
    const isTemplateNameValid = validateTemplateName();
    const isTemplateIdValid = validateTemplateId();
    const isTemplateTypeValid = validateTemplateType();
    const isSenderIdValid = validateSenderId();
    const isSmsContentValid = validateSmsContent();

    // Check for external dependencies
    let isEntityIdsValid = true;
    let isSenderIdsValid = true;

    if (!entityIds || entityIds.length === 0) {
      toast.error("No entity found. Please add an entity first.", {
        position: "top-right",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      isEntityIdsValid = false;
    }

    if (!senderIds || senderIds.length === 0) {
      toast.error("No sender found. Please add a sender first.", {
        position: "top-right",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      isSenderIdsValid = false;
    }

    // Final validation check
    const isValid =
      isTemplateNameValid &&
      isTemplateIdValid &&
      isTemplateTypeValid &&
      isSenderIdValid &&
      isSmsContentValid &&
      isEntityIdsValid &&
      isSenderIdsValid;

    if (!isValid) return;

    const data = {
      username: loggedInUserName,
      customerCode: customerCode,
      customerName: name,
      templateName: templateName,
      templateId: templateId,
      templateType: templateType,
      entityId: entityIds,
      sendorId: selectedSenderId,
      smsDesc: smsDesc,
      smsContent: smsContent,
    };

    try {
      const response = await axiosPostApi(apiList.DLT_ADD_TEMPLATE, data);

      if (response?.status === 200 && response?.data?.status === true) {
        toast.success("Template added successfully!", {
          position: "top-right",
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
        handleClose();
        clearState();
        setDropDownOpen(false);
      } else if (response?.data?.respCode === "04") {
        CustomAlert({
          title: "Failed to Add Template",
          text: response?.data?.message,
          icon: "error",
          confirmButtonText: "Retry",
        });
      } else {
        const errorFields = response.data.data.errorFields;
        const errorMessages = Object.values(errorFields).join("\n");
        CustomAlert({
          title: "Failed to Add Template",
          text:
            errorMessages ||
            "There was an issue processing the request. Please try again.",
          icon: "error",
          confirmButtonText: "Retry",
        });
      }
    } catch (error) {
      CustomAlert({
        title: "Error",
        text: "There was an issue processing the request.",
        icon: "error",
        confirmButtonText: "Retry",
      });
    }
  };

  const handleTemplateTypeChange = (e) => {
    setTemplateType(e.target.value);
    setTemplateTypeError("");
  };

  const clearState = () => {
    setSelectedSenderId(""); // Reset selected sender ID
    setTemplateName(""); // Clear template name
    setTemplateId(""); // Clear template ID
    setTemplateType(""); // Clear template type
    setSmsDesc(""); // Clear SMS description
    setSmsContent(""); // Clear SMS content
    setSenderIdError(""); // Reset any previous error messages
    setTemplateNameError(""); // Reset template name error
    setTemplateIdError(""); // Reset template ID error
    setTemplateTypeError(""); // Reset template type error
    setSmsContentError(""); // Reset SMS content error
  };

  const handleBack = () => {
    handleClose();
    setSelectedSenderId(""); // Reset selected sender ID
    setTemplateId(""); // Clear template ID
    setTemplateType(""); // Clear template type
    if (modalType !== "modify") {
      setTemplateName(""); // Clear template name
      setSmsDesc(""); // Clear SMS description
      setSmsContent(""); // Clear SMS content
    }
    setSenderIdError(""); // Reset any previous error messages
    setTemplateNameError(""); // Reset template name error
    setTemplateIdError(""); // Reset template ID error
    setTemplateTypeError(""); // Reset template type error
    setSmsContentError(""); // Reset SMS content error
    setDropDownOpen(false);
  };

  const handleModifyData = async (entity) => {
    if (!templateName) {
      setTemplateNameError("Template Name is required");
      return;
    } else if (templateName.length < 2 || templateName.length > 50) {
      setTemplateNameError("Template Name must be 2 to 50 characters long");
      return;
    } else {
      setTemplateNameError("");
    }

    if (!TempDetails?.type) {
      setTemplateTypeError("Select Template type...");
      return;
    } else {
      setTemplateIdError("");
    }

    if (!smsContent) {
      setSmsContentError("Template Content is required");
      return;
    } else if (smsContent.length < 3 || smsContent.length > 300) {
      setSmsContentError("Template Content must be 3 to 300 characters long");
      return;
    } else {
      setSmsContentError("");
    }

    const data = {
      username: loggedInUserName,
      templateId: TempDetails?.tempId,
      templateName: templateName,
      templateType: templateType || TempDetails?.type,
      sendorId: TempDetails?.id,
      customerCode: TempDetails?.customerCode,
      customerName: TempDetails?.id,
      entityId: TempDetails?.entityId,
      smsContent: smsContent,
      smsDesc: smsDesc,
    };
    try {
      setLoading(true);
      const response = await axiosPostApi(apiList.MODIFY_TEMPLATE, data);
      if (response?.status === 200 && response.data.status === true) {
        toast.success("Template Update Successfully!", {
          position: "top-right",
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
        handleClose();
        handleShowTable();
        setDropDownOpen(false);
      } else {
        // Handle failure case
        CustomAlert({
          title: "Failed to Modify Data",
          text:
            response.data.message ||
            "There was an issue processing the request. Please try again.",
          icon: "error",
          confirmButtonText: "Retry",
        });
        handleClose();
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      CustomAlert({
        title: "Error",
        text: "Something went wrong. Please try again later.",
        icon: "error",
        confirmButtonText: "Retry",
      });
    }
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    modalType === "modify" ? handleModifyData() : handleAddTemplate(e);
  };

  useEffect(() => {
    if (modalType === "modify") {
      setTemplateName(TempDetails?.name);
      setSmsDesc(TempDetails?.smsDesc);
      setSmsContent(TempDetails?.smsContent);
    }
  }, [
    modalType,
    TempDetails?.name,
    TempDetails?.smsDesc,
    TempDetails?.smsContent,
  ]);

  return (
    <div
      className={`modal fade ${show ? "show d-block" : ""}`}
      tabIndex="-1"
      role="dialog"
      style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
    >
      <div className="modal-dialog modal-md" role="document">
        <div className="modal-content rounded-[16px]">
          <img
            src={LeftCircle}
            alt="leftImg"
            className="absolute left-0 bottom-0 w-[110px] h-[105px]"
          />
          <img
            src={RightCircle}
            alt="leftImg"
            className="absolute right-0 top-0 w-[110px] h-[105px]"
          />
          <div className="modal-header border-b-0 d-flex align-items-center">
            <img src={AddUserIcon} alt="add-user" className="h-[24px] mr-4" />
            <h5 className="modal-title font-medium main-title m-0">
              {modalType === "view"
                ? "View Template Detail"
                : modalType === "modify"
                ? "Modify Template Detail"
                : "Add Template ID"}
            </h5>
            <button
              type="button"
              className="btn-close ms-auto cursor-pointer z-20"
              aria-label="Close"
              onClick={handleBack}
            ></button>
          </div>
          <form onSubmit={handleFormSubmit}>
            {errorMessage && (
              <div className="alert alert-danger" role="alert">
                {errorMessage}
              </div>
            )}
            <div className="modal-body pb-0">
              <div className="row mb-3">
                <div className="col-md-6">
                  <label htmlFor="entityId" className="form-label">
                    Entity ID<span style={{ color: "#DC3545" }}>*</span>
                  </label>
                  <input
                    type="text"
                    id="entityId"
                    className="border !border-[#495057] !rounded-[5px] px-2 py-1 w-full h-[33px]"
                    value={
                      entityIds.length > 0 ? entityIds : TempDetails?.entityId
                    }
                    readOnly
                    style={{ cursor: "not-allowed" }}
                  />
                </div>
                <div className="col-md-6">
                  <label htmlFor="senderId" className="form-label">
                    Sender ID<span style={{ color: "#DC3545" }}>*</span>
                  </label>
                  {modalType === "view" || modalType === "modify" ? (
                    <input
                      type="text"
                      className="border !border-[#495057] !rounded-[5px] px-2 py-1 w-full h-[33px]"
                      value={TempDetails?.id || "N/A"}
                      readOnly
                      style={{ cursor: isReadOnly ? "not-allowed" : "auto" }}
                    />
                  ) : (
                    <select
                      id="senderId"
                      className="border !border-[#495057] !rounded-[5px] px-2 py-1 w-full h-[33px]"
                      value={
                        modalType === "view"
                          ? TempDetails?.id
                          : selectedSenderId
                      }
                      onChange={handleSenderIdChange}
                      style={{ appearance: "listbox" }}
                      readOnly={isReadOnly}
                    >
                      <option value="" disabled>
                        Select Sender ID
                      </option>
                      {senderIds?.map((id) => (
                        <option key={id} value={id}>
                          {id}
                        </option>
                      ))}
                    </select>
                  )}
                  {senderIdError && (
                    <div className="text-danger">{senderIdError}</div>
                  )}
                </div>
              </div>
              <div className="row mb-3">
                <div className="col-md-6">
                  <label htmlFor="templateName" className="form-label">
                    Template Name<span style={{ color: "#DC3545" }}>*</span>
                  </label>
                  <input
                    type="text"
                    id="templateName"
                    className="border !border-[#495057] !rounded-[5px] px-2 py-1 w-full h-[33px]"
                    placeholder="Enter Template Name"
                    value={
                      modalType === "view" ? TempDetails?.name : templateName
                    }
                    onChange={(e) => setTemplateName(e.target.value)}
                    autoComplete="off"
                    readOnly={modalType === "view"}
                  />
                  {templateNameError && modalType !== "view" && (
                    <div className="text-danger">{templateNameError}</div>
                  )}
                </div>
                <div className="col-md-6">
                  <label htmlFor="templateId" className="form-label">
                    Template ID<span style={{ color: "#DC3545" }}>*</span>
                  </label>
                  <input
                    type="text"
                    id="templateId"
                    className="border !border-[#495057] !rounded-[5px] px-2 py-1 w-full h-[33px]"
                    placeholder="Enter Template ID"
                    value={isReadOnly ? TempDetails?.tempId : templateId}
                    onChange={(e) => setTemplateId(e.target.value)}
                    readOnly={isReadOnly}
                    autoComplete="off"
                    style={{ cursor: isReadOnly ? "not-allowed" : "auto" }}
                  />
                  {templateIdError && (
                    <div className="text-danger">{templateIdError}</div>
                  )}
                </div>
              </div>
              <div className="mb-3">
                <label htmlFor="selectType" className="form-label">
                  Select Type<span style={{ color: "#DC3545" }}>*</span>
                </label>
                {modalType === "view" ? (
                  <input
                    type="selectType"
                    className="border !border-[#495057] !rounded-[5px] px-2 py-1 w-full h-[33px]"
                    value={TempDetails?.type || "N/A"}
                    readOnly
                  />
                ) : (
                  <select
                    id="selectType"
                    className="border !border-[#495057] !rounded-[5px] px-2 py-1 w-full h-[33px]"
                    value={
                      modalType === "view" ? TempDetails?.type : templateType
                    }
                    onChange={handleTemplateTypeChange}
                    onClick={() => setDropDownOpen(true)}
                    style={{ appearance: "listbox" }}
                  >
                    <option value="" disabled={modalType === "view"}>
                      {modalType === "modify" && !dropDownOpen
                        ? TempDetails?.type
                        : "Select type"}
                    </option>
                    <option value="Transactional">Transactional</option>
                    <option value="Promotional">Promotional</option>
                  </select>
                )}
                {templateTypeError && modalType !== "view" && (
                  <div className="text-danger">{templateTypeError}</div>
                )}
              </div>
              <div className="mb-3">
                <label htmlFor="description" className="form-label">
                  Description
                </label>
                <textarea
                  id="description"
                  className="border !border-[#495057] !rounded-[5px] px-2 py-1 w-full"
                  placeholder="Enter Description"
                  rows="2"
                  value={modalType === "view" ? TempDetails?.smsDesc : smsDesc}
                  onChange={(e) => setSmsDesc(e.target.value)}
                  autoComplete="off"
                  readOnly={modalType === "view"}
                ></textarea>
              </div>
              <div className="mb-3">
                <label htmlFor="content" className="form-label">
                  Enter Content<span style={{ color: "#DC3545" }}>*</span>
                </label>
                <textarea
                  id="content"
                  className="border !border-[#495057] !rounded-[5px] px-2 py-1 w-full"
                  placeholder="Enter Content"
                  rows="2"
                  value={
                    modalType === "view" ? TempDetails?.smsContent : smsContent
                  }
                  onChange={(e) => setSmsContent(e.target.value)}
                  autoComplete="off"
                  readOnly={modalType === "view"}
                ></textarea>
                {smsContentError && modalType !== "view" && (
                  <div className="text-danger">{smsContentError}</div>
                )}
              </div>
            </div>
            <div className="modal-footer border-t-0 d-flex justify-content-center pt-0 pb-4 gap-3">
              <button
                type="button"
                className="flex btn btn-back w-[133px] h-[33px] !rounded-[8px] !items-center !justify-center !font-medium"
                onClick={handleBack}
              >
                Cancel
              </button>
              {(modalType === "modify" && modalType !== "view") ||
              !modalType ? (
                <button
                  type="submit"
                  className="flex btn btn-submit w-[133px] h-[33px] !rounded-[8px] !items-center !justify-center !font-medium"
                >
                  {loading ? (
                    <CircularProgress
                      className="circular-loader"
                      style={{ width: "20px", height: "20px" }}
                    />
                  ) : modalType === "modify" ? (
                    "Update"
                  ) : (
                    "Add"
                  )}
                </button>
              ) : null}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TemplateModal;
